import type { MergedMarketRecord } from '@/types/market';
import type { BenchmarkStats, GeoInsights, ScoredGeography } from '@/types/insights';
import { zScore } from '@/utils/numbers';
import { buildBenchmark } from './benchmark';
import { computeHotColdScore } from './hotColdScore';
import { computeSellSpeedShift } from './sellSpeedShift';
import { computeInventoryPressure } from './inventoryPressure';
import { computeOpportunityScore } from './opportunityScore';
import { computeCoolingRisk } from './coolingRisk';
import { computeBuyerCompetition } from './buyerCompetition';
import { computeConfidenceScore } from './confidenceScore';
import { buildExplanations } from '@/engine/explain/explanationBuilder';

export { buildBenchmark };

function computeRawZScores(record: MergedMarketRecord, benchmark: BenchmarkStats): Record<string, number | undefined> {
  const row = record.resolved;
  return {
    medianDaysOnMarket: zScore(row.medianDaysOnMarket, benchmark.meanByField.medianDaysOnMarket, benchmark.stdByField.medianDaysOnMarket),
    monthsOfInventory: zScore(row.monthsOfInventory, benchmark.meanByField.monthsOfInventory, benchmark.stdByField.monthsOfInventory),
    saleToListRatioPct: zScore(row.saleToListRatioPct, benchmark.meanByField.saleToListRatioPct, benchmark.stdByField.saleToListRatioPct),
    priceReductionSharePct: zScore(
      row.priceReductionSharePct,
      benchmark.meanByField.priceReductionSharePct,
      benchmark.stdByField.priceReductionSharePct,
    ),
    medianSalePriceYoyPct: zScore(
      row.medianSalePriceYoyPct,
      benchmark.meanByField.medianSalePriceYoyPct,
      benchmark.stdByField.medianSalePriceYoyPct,
    ),
  };
}

/** Scores a single already-merged geography record against a precomputed benchmark. */
export function scoreGeography(record: MergedMarketRecord, benchmark: BenchmarkStats, isSampleData: boolean): GeoInsights {
  const row = record.resolved;

  const hotColdScore = computeHotColdScore(row, benchmark);
  const sellSpeedShift = computeSellSpeedShift(row);
  const inventoryPressure = computeInventoryPressure(row);
  const opportunityScore = computeOpportunityScore(row, benchmark);
  const coolingRisk = computeCoolingRisk(row);
  const buyerCompetition = computeBuyerCompetition(row, benchmark);
  const confidenceScore = computeConfidenceScore(record);
  const rawZScores = computeRawZScores(record, benchmark);

  const insights: GeoInsights = {
    geoId: record.geoId,
    geoName: record.geoName,
    periodLabel: record.periodLabel,
    hotColdScore,
    sellSpeedShift,
    inventoryPressure,
    opportunityScore,
    coolingRisk,
    buyerCompetition,
    confidenceScore,
    rawZScores,
    explanations: [],
    contributingSources: record.contributingSources,
    isSampleData,
  };

  insights.explanations = buildExplanations(record, insights);
  return insights;
}

/** Scores every merged record in one pass, sharing a single statewide benchmark. */
export function scoreAllGeographies(records: MergedMarketRecord[], isSampleData: boolean): ScoredGeography[] {
  const benchmark = buildBenchmark(records);
  return records.map((record) => ({
    record,
    insights: scoreGeography(record, benchmark, isSampleData),
  }));
}
